<template>
  <!--implemented for webrtc and webex telephony only-->

  <div v-show="stream" ref="container" class="plr-voicewave-container">
    <canvas ref="canvas" class="pt-remote-audio-animation position-absolute" />
  </div>
</template>

<script lang="ts">
import { useStore } from "vuex";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { TelephonyType } from "@moderrotech/servers-communication-library/src/types";
import {
  ResizeObserverEntry,
  useResizeObserver,
  useUserMedia
} from "@vueuse/core";
import _ from "lodash";
import { useAVMedia } from "@/composables/customAV";

export default {
  setup() {
    const store = useStore();

    const canvas = ref(null),
      container = ref(null);

    const pixelsPerLine = 5;

    const telephonyType = computed(() => store.state.telephonyType);

    const props = useAVMedia(canvas, {
      type: "frequ",
      frequDirection: "mo",
      lineColor: "#4DFDE9"
    });

    const { stream: stagingStream, stop: stopStagingStream } = useUserMedia({
      enabled: store.state.config.config.envMode === "staging"
    });

    const stream = computed(
      () =>
        stagingStream.value ||
        (telephonyType.value === TelephonyType.webex
          ? store.state.webex.streams.remoteVideo
          : telephonyType.value === TelephonyType.webrtc
          ? store.state.webrtc.streams.remote
          : null)
    );

    watch(
      () => stream.value,
      v => {
        (props as { media: MediaStream | null }).media = v;
      },
      { immediate: true }
    );

    onBeforeUnmount(() => stopStagingStream());

    onMounted(() => {
      useResizeObserver(
        container.value,
        (entries: ReadonlyArray<ResizeObserverEntry>) => {
          if (!_.first(entries)) {
            return;
          }

          (props as { canvWidth: number | null }).canvWidth =
            entries[0].contentRect.width;
          (props as { canvHeight: number | null }).canvHeight =
            entries[0].contentRect.height;
          (props as { frequLnum: number | null }).frequLnum =
            entries[0].contentRect.width / pixelsPerLine;
        }
      );
    });

    return {
      container,
      canvas,
      stream
    };
  }
};
</script>
<style lang="scss">
$bottom-offset: 40px;

.plr-voicewave-container {
  position: absolute;
  bottom: $bottom-offset;
  left: 0;

  width: 100vw;
  height: 100px;

  display: inline-flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;

  padding: 0;
  background: linear-gradient(
    0deg,
    hsla(240, 80%, 40%, 0.2) 0%,
    hsla(240, 80%, 30%, 0.5) 25%,
    hsla(255, 100%, 50%, 0.99) 50%,
    hsla(240, 80%, 30%, 0.5) 75%,
    hsla(240, 80%, 40%, 0.2) 100%
  );
  box-shadow: 0 0 20px 20px hsla(240, 80%, 40%, 0.2);

  &::before {
    content: "";
    display: inline-block;
    width: 100%;
    height: 1px;
    position: absolute;
    top: 50%;
    left: 0;
    background: linear-gradient(
      90deg,
      hsla(173, 98%, 65%, 0) 0%,
      hsla(173, 98%, 65%, 1) 50%,
      hsla(173, 98%, 65%, 0) 100%
    );
    transform: translateY(-0.5px);
  }
}
.pt-remote-audio-animation {
  display: inline-block;
  position: static;

  //width: 100%;
  mix-blend-mode: plus-lighter;
  //backdrop-filter: blur(4px);
}
</style>
